import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { requiredTrimValidator } from 'src/app/shared/validators/fields-validators';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { DEFAULT_LABEL_COLOR, ILabel, LABEL_COLOR_PALETTE } from '../../../../models/settings.models';

export interface LabelFormDialogData {
  label: ILabel | null;
}

export interface LabelFormPayload {
  name: string;
  description: string;
  color: string;
  visible: boolean;
  eligible: boolean;
}

@Component({
  selector: 'app-label-form-dialog',
  templateUrl: './label-form-dialog.component.html',
  styleUrls: ['./label-form-dialog.component.scss'],
})
export class LabelFormDialogComponent implements OnInit, OnDestroy {
  /** The parent owns the store; this dialog only reports what the user filled in. */
  @Output() save = new EventEmitter<LabelFormPayload>();

  label: ILabel | null = null;

  readonly labelNameControl = new FormControl('', requiredTrimValidator());
  readonly labelDescriptionControl = new FormControl('');
  readonly visibleControl = new FormControl(true);
  readonly eligibleControl = new FormControl(true);

  selectedColor = DEFAULT_LABEL_COLOR;
  isSubmitting = false;

  readonly palette = LABEL_COLOR_PALETTE;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) data: LabelFormDialogData,
    private readonly dialogRef: MatDialogRef<LabelFormDialogComponent>,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.label = data?.label ?? null;
  }

  ngOnInit(): void {
    this.labelNameControl.setValue(this.label?.name ?? '');
    this.labelDescriptionControl.setValue(this.label?.description ?? '');
    // `?? true` and not `|| true`: `false` is a real stored value here, and `||`
    // would quietly turn every hidden label back on the moment it was opened.
    this.visibleControl.setValue(this.label?.visible ?? true);
    this.eligibleControl.setValue(this.label?.eligible ?? true);
    this.selectedColor = this.label?.color || DEFAULT_LABEL_COLOR;

    this.applyGovernancePairing(this.visibleControl.value ?? true);
    this.visibleControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => this.applyGovernancePairing(!!visible));

    // A provider_sync label answers 400 to a name or description change and accepts a
    // colour change on the same record. The form does not offer the first two at all
    // — see the template — and these controls are disabled so nothing can route round
    // the template and submit a value from them.
    if (this.isProviderSynced) {
      this.labelNameControl.disable();
      this.labelDescriptionControl.disable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isEditMode(): boolean {
    return !!this.label;
  }

  /**
   * `provider_sync` only. An `erp` label is fully editable in this release — the
   * backend refuses the name edit for provider labels alone, and locking ERP ones
   * too would be a UI-only restriction with nothing behind it.
   */
  get isProviderSynced(): boolean {
    return this.label?.origin === 'provider_sync';
  }

  get dialogTitle(): string {
    const action = this.isEditMode ? 'common.edit' : 'common.add';
    return `${this.translateService.instant(action)} ${this.translateService.instant('settings.label')}`;
  }

  get saveLabel(): string {
    return this.translateService.instant(this.isEditMode ? 'common.save' : 'common.add');
  }

  get isFormInvalid(): boolean {
    // A provider_sync name control is disabled, and a disabled control reports
    // `invalid === false` regardless of its value, so this stays correct without a
    // special case. The name itself is never sent for that origin.
    return this.labelNameControl.invalid;
  }

  get dialogOptions(): SharedDialogOptions {
    return {
      size: 'medium',
      cancelText: this.translateService.instant('common.cancel'),
      customConfirmButton: true,
      hideConfirmButton: true,
    };
  }

  selectColor(color: string): void {
    this.selectedColor = color;
  }

  isColorSelected(color: string): boolean {
    return this.selectedColor?.toUpperCase() === color?.toUpperCase();
  }

  trackByColor(_: number, color: string): string {
    return color;
  }

  submitForm(): void {
    this.labelNameControl.markAsTouched();
    if (this.isFormInvalid || this.isSubmitting) return;

    // `getRawValue`-equivalent: a disabled control's `.value` is still readable, and
    // for a provider_sync label the parent drops the name before it reaches the wire.
    const name = (this.labelNameControl.value ?? '').trim();
    if (!name) return;

    this.isSubmitting = true;
    this.save.emit({
      name,
      description: (this.labelDescriptionControl.value ?? '').trim(),
      color: this.selectedColor,
      visible: !!this.visibleControl.value,
      // Read off the control rather than recomputed: `applyGovernancePairing` has
      // already forced it to `false` whenever `visible` is off, so recomputing here
      // would be a second place for the invariant to live and disagree.
      eligible: !!this.eligibleControl.value,
    });
  }

  close(): void {
    if (this.isSubmitting) return;
    this.dialogRef.close();
  }

  setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
    this.cdr.markForCheck();
  }

  /**
   * The GOV-04 pair, made unreachable rather than merely validated.
   *
   * The backend refuses eligible-but-hidden with a 400, so two independent switches
   * let a user walk into an error by clicking in the wrong order. Instead: while
   * `visible` is off, `eligible` is forced off and its control is DISABLED, which is
   * what puts the invalid combination out of reach. Handling the 400 is the fallback,
   * not the design.
   *
   * `emitEvent: false` on the forced write: `eligible` has no listener today, but a
   * later one would otherwise see a change the user did not make.
   */
  private applyGovernancePairing(visible: boolean): void {
    if (visible) {
      this.eligibleControl.enable({ emitEvent: false });
      return;
    }

    this.eligibleControl.setValue(false, { emitEvent: false });
    this.eligibleControl.disable({ emitEvent: false });
  }
}
